import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { TenantDateField } from '../../../components/TenantDateField';
import { ArrowBackRounded, ArrowForwardRounded, PlayArrowRounded } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, DocumentData } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { fetchSectionsOverview } from './section-picker.data';
import { useTenantStore } from '../../../stores/tenant.store';
import { errorText } from '../../../lib/error-text';
import {
  courseTemplateApi,
  type SyllabusDatePreviewRowDto,
  type AssignmentDatePreviewRowDto,
} from '../../../lib/api/course-template.api';
import { formatDate } from '../../../lib/i18n-format';
import { RegenerationPreview, fitNeedsConfirmation } from './RegenerationPreview';
import type { SyllabusRegenerationPreviewDto } from '../../../lib/api/course-template.api';

const WEEKDAY_LABEL: Record<string, string> = {
  sunday: 'Dom',
  monday: 'Lun',
  tuesday: 'Mar',
  wednesday: 'Mié',
  thursday: 'Jue',
  friday: 'Vie',
  saturday: 'Sáb',
};

function formatDateEs(ymd: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return formatDate(dt, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface SectionOption {
  id: string;
  courseName: string;
  /** El curso de la sección: se compara con el de la plantilla. */
  courseId?: string;
  /** "BT-T04" — distingue dos secciones del mismo curso y período. */
  sectionCode?: string;
  /** Un período cerrado se oculta por defecto: generar ahí no sirve a nadie. */
  termClosed?: boolean;
  /** Cuántos alumnos verán lo que se genere. */
  enrolled?: number;
  termName: string;
  termStartDate?: string;
  termEndDate?: string;
  sectionStartDate?: string;
  sectionEndDate?: string;
  facultyName?: string;
}

interface Props {
  /** `courseId` es el del CURSO de la plantilla: se usa para no ofrecer por
   *  defecto secciones de otras materias. */
  template: { id: string; title: string; courseId?: string };
  onClose: () => void;
  onGenerated: () => void;
  /** Preselects (and locks) the target section — used when launched from /curso. */
  initialSectionId?: string;
}

export function GenerateSyllabusWizard({
  template,
  onClose,
  onGenerated,
  initialSectionId,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [sections, setSections] = useState<SectionOption[]>([]);
  const [sectionId, setSectionId] = useState(initialSectionId ?? '');
  /** Ver también las secciones de otras materias y de períodos cerrados. */
  const [showAllSections, setShowAllSections] = useState(false);
  const visibleSections = sections.filter(
    (s) =>
      showAllSections ||
      s.id === initialSectionId ||
      (!s.termClosed && (!template.courseId || s.courseId === template.courseId)),
  );
  const hiddenCount = sections.length - visibleSections.length;
  const selectedSection = sections.find((s) => s.id === sectionId);

  // Con una sola opción, elegirla a mano no es una decisión: es un trámite. Y
  // mientras no hay sección elegida el asistente no puede inferir las fechas ni
  // el docente, así que la pantalla se queda a medio llenar sin decir por qué.
  const onlyOption = visibleSections.length === 1 ? visibleSections[0]!.id : null;
  useEffect(() => {
    if (!sectionId && onlyOption) setSectionId(onlyOption);
  }, [sectionId, onlyOption]);
  const [instructorName, setInstructorName] = useState('');
  const [instructorCredentials, setInstructorCredentials] = useState('');
  // The instructor is the section's assigned faculty (Section.facultyId, the SSOT).
  // Shown read-only; the admin may override the display just for the generated PDF.
  const [overrideInstructor, setOverrideInstructor] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [modality, setModality] = useState<'in-person' | 'online' | 'hybrid'>('online');
  const [biblicalVersion, setBiblicalVersion] = useState('RV1960');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  // ── Wizard step state ────────────────────────────────────────────────────
  // Step 1 = settings. Step 2 = module day dates. Step 3 = assignment due dates.
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [previewRows, setPreviewRows] = useState<SyllabusDatePreviewRowDto[]>([]);
  const [assignmentRows, setAssignmentRows] = useState<AssignmentDatePreviewRowDto[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Map key: `${templateModuleId}::${dayIndex}` → ISO date the admin set in the table.
  const [dateOverrides, setDateOverrides] = useState<Record<string, string>>({});
  // Map key: templateAssignmentId → due date (YYYY-MM-DD) the admin set.
  const [assignmentOverrides, setAssignmentOverrides] = useState<Record<string, string>>({});
  // S5: what applying the template again does to the section's sessions.
  const [regen, setRegen] = useState<SyllabusRegenerationPreviewDto | null>(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [confirmProtected, setConfirmProtected] = useState(false);
  // S6·2: sesiones recortadas a menos de la mitad o que no caben.
  const [confirmFit, setConfirmFit] = useState(false);

  useEffect(() => {
    if (!currentTenantId) return;
    (async () => {
      const snap = await getDocs(query(collection(db, `tenants/${currentTenantId}/sections`)));
      const fb = await import('firebase/firestore');

      // Collect raw section data + unique ids to resolve in parallel.
      const activeSections = snap.docs
        .map((d) => ({ id: d.id, data: d.data() as DocumentData }))
        .filter((s) => s.data['status'] !== 'cancelled');

      const courseIds = new Set<string>();
      const termIds = new Set<string>();
      for (const s of activeSections) {
        if (s.data['courseId']) courseIds.add(s.data['courseId'] as string);
        if (s.data['termId']) termIds.add(s.data['termId'] as string);
      }

      // Parallel fetch of all referenced docs (deduped).
      const fetchDoc = (path: string) => fb.getDoc(fb.doc(db, path));
      const [courseDocs, termDocs, overview] = await Promise.all([
        Promise.all(
          [...courseIds].map((id) =>
            fetchDoc(`tenants/${currentTenantId}/courses/${id}`).then((doc) => ({ id, doc })),
          ),
        ),
        Promise.all(
          [...termIds].map((id) =>
            fetchDoc(`tenants/${currentTenantId}/terms/${id}`).then((doc) => ({ id, doc })),
          ),
        ),
        // EL NOMBRE DEL DOCENTE LO DA EL SERVIDOR (PR 3A), sin conteos: antes
        // se leía la ficha de cada titular, y la de OTRO docente no es legible
        // para quien genera. Si falla, el nombre queda vacío y se escribe a
        // mano (el paso ya lo valida); no tumba el asistente.
        fetchSectionsOverview(
          currentTenantId,
          activeSections.map((s) => s.id),
          { counts: false },
        ).catch(() => ({ facultyNames: new Map<string, string>() })),
      ]);

      const courseMap = new Map(
        courseDocs.filter((x) => x.doc.exists()).map((x) => [x.id, x.doc.data()!]),
      );
      const termMap = new Map(
        termDocs.filter((x) => x.doc.exists()).map((x) => [x.id, x.doc.data()!]),
      );

      const toIsoDate = (raw: unknown): string | undefined => {
        if (!raw) return undefined;
        if (typeof raw === 'string') {
          // already ISO yyyy-MM-dd or full ISO datetime
          return raw.slice(0, 10);
        }
        // Firestore Timestamp
        if (typeof (raw as { toDate?: unknown }).toDate === 'function') {
          const d = (raw as { toDate(): Date }).toDate();
          return d.toISOString().slice(0, 10);
        }
        // DateOnly value object serialised as { _value: 'yyyy-MM-dd' } or { value: ... }
        const v = raw as { _value?: unknown; value?: unknown };
        if (typeof v._value === 'string') return (v._value as string).slice(0, 10);
        if (typeof v.value === 'string') return (v.value as string).slice(0, 10);
        return undefined;
      };

      const rows: SectionOption[] = activeSections.map((s) => {
        const course = courseMap.get(s.data['courseId'] as string);
        const term = termMap.get(s.data['termId'] as string);
        const fName = overview.facultyNames.get(s.id) ?? '';
        const termStart = toIsoDate(term?.['startDate']);
        const termEnd = toIsoDate(term?.['endDate']);
        const secStart = toIsoDate(s.data['startDate']);
        const secEnd = toIsoDate(s.data['endDate']);
        return {
          id: s.id,
          courseName: (course?.['name'] as string) ?? (s.data['courseId'] as string) ?? '',
          ...(s.data['courseId'] ? { courseId: s.data['courseId'] as string } : {}),
          ...(s.data['sectionCode'] ? { sectionCode: s.data['sectionCode'] as string } : {}),
          ...(term?.['status'] === 'closed' ? { termClosed: true } : {}),
          ...(typeof s.data['currentEnrollment'] === 'number'
            ? { enrolled: s.data['currentEnrollment'] as number }
            : {}),
          termName: (term?.['name'] as string) ?? (s.data['termId'] as string) ?? '',
          ...(termStart ? { termStartDate: termStart } : {}),
          ...(termEnd ? { termEndDate: termEnd } : {}),
          ...(secStart ? { sectionStartDate: secStart } : {}),
          ...(secEnd ? { sectionEndDate: secEnd } : {}),
          ...(fName ? { facultyName: fName } : {}),
        };
      });

      rows.sort((a, b) => a.courseName.localeCompare(b.courseName));
      setSections(rows);
    })();
  }, [currentTenantId]);

  // Auto-fill dates + instructor when the section changes. The instructor tracks
  // the section's assigned faculty (SSOT); a section switch resets any PDF override.
  useEffect(() => {
    const sec = sections.find((s) => s.id === sectionId);
    if (!sec) return;
    const inferredStart = sec.sectionStartDate ?? sec.termStartDate ?? '';
    const inferredEnd = sec.sectionEndDate ?? sec.termEndDate ?? '';
    if (inferredStart && !startDate) setStartDate(inferredStart);
    if (inferredEnd && !endDate) setEndDate(inferredEnd);
    setOverrideInstructor(false);
    setInstructorName(sec.facultyName ?? '');
    // Las credenciales no viven en la ficha: se escriben en el paso.
    setInstructorCredentials('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, sections]);

  // Validate step 1 then load the date preview and advance to step 2.
  const handleNextToPreview = async () => {
    if (!currentTenantId) return;
    if (!sectionId) {
      setErr('Selecciona una sección');
      return;
    }
    if (!instructorName.trim()) {
      setErr('Nombre del instructor obligatorio');
      return;
    }
    if (!startDate || !endDate) {
      setErr('Rango de fechas obligatorio');
      return;
    }
    setErr(null);
    setLoadingPreview(true);
    try {
      const { rows, assignmentRows: aRows } = await courseTemplateApi.previewSyllabusDates({
        tenantId: currentTenantId,
        templateId: template.id,
        sectionId,
        termStart: startDate,
      });
      setPreviewRows(rows);
      setAssignmentRows(aRows);
      // Pre-populate overrides with existing overridden dates so the admin
      // sees the actual scheduled date (not the recomputed math) in the input.
      const seeded: Record<string, string> = {};
      for (const r of rows) {
        if (r.alreadyOverridden && r.existingOverrideDate) {
          seeded[`${r.templateModuleId}::${r.dayIndex}`] = r.existingOverrideDate;
        }
      }
      setDateOverrides(seeded);
      // Seed assignment overrides from existing manual due dates.
      const seededA: Record<string, string> = {};
      for (const a of aRows) {
        if (a.alreadyOverridden && a.existingDate) {
          seededA[a.templateAssignmentId] = a.existingDate;
        }
      }
      setAssignmentOverrides(seededA);
      setStep(2);
    } catch (e) {
      setErr(errorText(e, 'No se pudo calcular el preview de fechas'));
    } finally {
      setLoadingPreview(false);
    }
  };

  // Build override payload — only include rows where the admin's pick
  // differs from the math-derived date. Pre-existing overrides stay
  // (server preserves them via dateOverridden=true) but we still
  // include them so the flag round-trips after re-runs.
  const buildModuleDates = () =>
    previewRows
      .map((r) => {
        const key = `${r.templateModuleId}::${r.dayIndex}`;
        const picked = dateOverrides[key];
        if (!picked) return null;
        if (picked === r.computedDate && !r.alreadyOverridden) return null;
        return {
          templateModuleId: r.templateModuleId,
          dayIndex: r.dayIndex,
          date: picked,
        };
      })
      .filter((x): x is { templateModuleId: string; dayIndex: number; date: string } => !!x);

  // S5: before the last step, show what happens to the section's sessions.
  const goToStep3 = async () => {
    setStep(3);
    if (!currentTenantId) return;
    setRegen(null);
    setConfirmProtected(false);
    setConfirmFit(false);
    setRegenLoading(true);
    try {
      const moduleDates = buildModuleDates();
      const p = await courseTemplateApi.previewRegeneration({
        tenantId: currentTenantId,
        templateId: template.id,
        sectionId,
        termRange: { startDate, endDate },
        ...(moduleDates.length > 0 ? { moduleDates } : {}),
      });
      setRegen(p);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setRegenLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    setSubmitting(true);
    setErr(null);
    try {
      const moduleDates = buildModuleDates();

      // Assignment overrides — include a row only when the admin's pick differs
      // from the computed date, or a manual override already existed (so the
      // flag round-trips across regenerations).
      const assignmentDates = assignmentRows
        .map((a) => {
          const picked = assignmentOverrides[a.templateAssignmentId];
          if (!picked) return null;
          if (picked === a.computedDate && !a.alreadyOverridden) return null;
          return { templateAssignmentId: a.templateAssignmentId, date: picked };
        })
        .filter((x): x is { templateAssignmentId: string; date: string } => !!x);

      const res = await courseTemplateApi.generateSyllabus({
        tenantId: currentTenantId,
        templateId: template.id,
        sectionId,
        instructor: {
          name: instructorName.trim(),
          ...(instructorCredentials.trim() ? { credentials: instructorCredentials.trim() } : {}),
        },
        termRange: { startDate, endDate },
        modality,
        biblicalVersion,
        ...(moduleDates.length > 0 ? { moduleDates } : {}),
        ...(assignmentDates.length > 0 ? { assignmentDates } : {}),
        ...(confirmProtected ? { confirmProtected: true } : {}),
        ...(confirmFit ? { confirmFitIssues: true } : {}),
      });
      setResult(
        t('templateEditor.regen.result', {
          updated: res.sessionsUpdated,
          created: res.sessionsCreated,
          removed: res.sessionsRemoved,
          kept: res.sessionsKeptProtected ?? 0,
          keyTexts: res.sessionsKeyTextsUpdated ?? 0,
          removedAtt: res.sessionsRemovedOnAttendanceDay ?? 0,
          skipped: res.sessionsSkippedOnAttendanceDate ?? 0,
          modulesCreated: res.modulesCreated,
          modulesUpdated: res.modulesUpdated,
          assignmentsCreated: res.assignmentsCreated,
          assignmentsUpdated: res.assignmentsUpdated,
        }),
      );
    } catch (e) {
      setErr(errorText(e, 'Error al generar'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('templateEditor.wizard.title', { template: template.title })}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step - 1} sx={{ mt: 1, mb: 2 }}>
          <Step>
            <StepLabel>{t('templateEditor.wizard.step1')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('templateEditor.wizard.step2')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('templateEditor.wizard.step3')}</StepLabel>
          </Step>
        </Stepper>

        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {result}
          </Alert>
        )}

        {step === 1 && (
          <Stack spacing={2} mt={1}>
            <FormControl size="small">
              <InputLabel>{t('templateEditor.wizard.targetSection')}</InputLabel>
              <Select
                label={t('templateEditor.wizard.targetSection')}
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                disabled={!!initialSectionId}
              >
                {visibleSections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.sectionCode ? `${s.sectionCode} · ` : ''}
                    {s.courseName} — {s.termName}
                    {s.facultyName ? ` · ${s.facultyName}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* La plantilla pertenece a un curso, así que por defecto sólo se
                ofrecen las secciones DE ESE CURSO y de períodos abiertos.
                Generar el plan de Teología IV dentro de Griego Básico es un error
                que nadie nota hasta que los alumnos ven tareas que no son suyas,
                y hasta ahora la lista ofrecía todas las secciones del seminario
                sin distinguir. No se bloquea —puede haber casos legítimos— pero
                hay que pedirlo. */}
            {hiddenCount > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showAllSections}
                    onChange={(e) => setShowAllSections(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    {t('templateEditor.wizard.showAllSections', { count: hiddenCount })}
                  </Typography>
                }
              />
            )}
            {selectedSection &&
              template.courseId &&
              selectedSection.courseId !== template.courseId && (
                <Alert severity="warning" variant="outlined">
                  {t('templateEditor.wizard.otherCourse', {
                    course: selectedSection.courseName,
                  })}
                </Alert>
              )}

            <Stack direction="row" spacing={1}>
              <TenantDateField label="Inicio" value={startDate} onChange={setStartDate} fullWidth />
              <TenantDateField label="Fin" value={endDate} onChange={setEndDate} fullWidth />
            </Stack>

            {(() => {
              const sec = sections.find((s) => s.id === sectionId);
              // Sin sección elegida no hay nada que decir del docente: el aviso
              // de "no tiene docente asignado" afirmaba algo falso sobre una
              // sección que el usuario todavía no había elegido.
              if (!sec) return null;
              const hasFaculty = !!sec.facultyName;
              // Read-only: the instructor is the section's assigned faculty (SSOT).
              if (hasFaculty && !overrideInstructor) {
                return (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Instructor (docente de la sección)
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {instructorName}
                          {instructorCredentials ? ` · ${instructorCredentials}` : ''}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => setOverrideInstructor(true)}>
                        Editar solo para el PDF
                      </Button>
                    </Stack>
                  </Box>
                );
              }
              // Editable: no faculty on the section, or an explicit PDF override.
              return (
                <>
                  {!hasFaculty && (
                    <Typography variant="caption" color="warning.main">
                      La sección no tiene docente asignado. Asignalo en Secciones; el nombre aquí es
                      solo para el PDF.
                    </Typography>
                  )}
                  <TextField
                    size="small"
                    label="Instructor"
                    value={instructorName}
                    onChange={(e) => setInstructorName(e.target.value)}
                  />
                  <TextField
                    size="small"
                    label="Credenciales (opcional)"
                    value={instructorCredentials}
                    onChange={(e) => setInstructorCredentials(e.target.value)}
                    placeholder="M.M.B."
                  />
                  {hasFaculty && (
                    <Button
                      size="small"
                      onClick={() => {
                        setOverrideInstructor(false);
                        setInstructorName(sec?.facultyName ?? '');
                        setInstructorCredentials('');
                      }}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Volver al docente de la sección
                    </Button>
                  )}
                </>
              );
            })()}

            <Stack direction="row" spacing={1}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Modalidad</InputLabel>
                <Select
                  label="Modalidad"
                  value={modality}
                  onChange={(e) => setModality(e.target.value as 'in-person' | 'online' | 'hybrid')}
                >
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="in-person">Presencial</MenuItem>
                  <MenuItem value="hybrid">Híbrida</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Versión bíblica"
                value={biblicalVersion}
                onChange={(e) => setBiblicalVersion(e.target.value)}
                sx={{ width: 140 }}
              />
            </Stack>

            <Typography variant="caption" color="text.secondary">
              En el siguiente paso podrás ver las fechas computadas para cada día de cada módulo y
              ajustarlas antes de generar. El generador es idempotente.
            </Typography>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={1.5} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Estas son las fechas que se generarán para cada día de cada módulo, calculadas a
              partir del inicio del período. Ajusta cualquier fila para sobrescribir la fecha; ese
              override se conservará cuando regeneres el syllabus.
            </Typography>
            <Box
              sx={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {previewRows.map((r, i) => {
                const key = `${r.templateModuleId}::${r.dayIndex}`;
                const value = dateOverrides[key] ?? r.computedDate;
                const changed = value !== r.computedDate;
                return (
                  <Stack
                    key={key}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderBottom:
                        i === previewRows.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)',
                      bgcolor: r.alreadyOverridden
                        ? 'rgba(245,158,11,0.06)'
                        : changed
                          ? 'rgba(99,102,241,0.05)'
                          : 'transparent',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {r.moduleName}
                        </Typography>
                        {r.alreadyOverridden && (
                          <Chip
                            size="small"
                            label="Override existente"
                            sx={{
                              height: 16,
                              fontSize: 10,
                              bgcolor: 'rgba(245,158,11,0.18)',
                              color: '#fbbf24',
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Día {r.dayIndex + 1} · Semana {r.weekIndex + 1} ·{' '}
                        {WEEKDAY_LABEL[r.weekday] ?? r.weekday}
                        {' · '}
                        calculado: {formatDateEs(r.computedDate)}
                      </Typography>
                    </Box>
                    <TenantDateField
                      size="small"
                      value={value}
                      onChange={(v) => setDateOverrides((prev) => ({ ...prev, [key]: v }))}
                    />
                  </Stack>
                );
              })}
              {previewRows.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                  La plantilla no tiene módulos con días definidos.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Tip: el día en que cae la fecha de inicio del período ancla todo el cómputo. Si algún
              día no coincide con el calendario real, ajústalo aquí.
            </Typography>
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={1.5} mt={1}>
            <RegenerationPreview
              preview={regen}
              loading={regenLoading}
              confirmProtected={confirmProtected}
              onConfirmProtected={setConfirmProtected}
              confirmFit={confirmFit}
              onConfirmFit={setConfirmFit}
            />
            <Typography variant="body2" color="text.secondary">
              {t('templateEditor.generate.datesHelp')}
            </Typography>
            {(() => {
              // Qué se va a crear y para quién. Este paso publica trabajo con
              // fecha a alumnos matriculados, y hasta ahora el asistente no lo
              // decía en ninguna parte.
              const sec = sections.find((x) => x.id === sectionId);
              if (!sec) return null;
              const end = sec.sectionEndDate ?? sec.termEndDate;
              const start = sec.sectionStartDate ?? sec.termStartDate;
              // Una entrega fuera del rango de la sección es casi siempre un
              // error de fechas —de la sección o del calendario del curso—, y
              // descubrirlo después significa corregirlo con los alumnos ya
              // mirando la tarea.
              const outside = assignmentRows.filter((a) => {
                const d = assignmentOverrides[a.templateAssignmentId] ?? a.computedDate;
                return (!!end && d > end) || (!!start && d < start);
              });
              return (
                <>
                  <Alert severity="info" variant="outlined">
                    {t('templateEditor.generate.summary', {
                      assignments: assignmentRows.length,
                      // `previewRows` son los DÍAS de jornada (6), no las
                      // sesiones de 45 minutos (32). Llamarlas "sesiones"
                      // afirmaba un número cinco veces menor al que se crea.
                      days: previewRows.length,
                      section: `${sec.courseName} · ${sec.termName}`,
                      count: sec.enrolled ?? 0,
                    })}
                  </Alert>
                  {outside.length > 0 && (
                    <Alert severity="warning" variant="outlined">
                      {t('templateEditor.generate.outsideRange', {
                        count: outside.length,
                        names: outside.map((a) => a.name).join(', '),
                        from: start ?? '—',
                        to: end ?? '—',
                      })}
                    </Alert>
                  )}
                </>
              );
            })()}
            <Box
              sx={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {assignmentRows.map((a, i) => {
                const value = assignmentOverrides[a.templateAssignmentId] ?? a.computedDate;
                const changed = value !== a.computedDate;
                return (
                  <Stack
                    key={a.templateAssignmentId}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderBottom:
                        i === assignmentRows.length - 1
                          ? 'none'
                          : '1px solid rgba(255,255,255,0.05)',
                      bgcolor: a.alreadyOverridden
                        ? 'rgba(245,158,11,0.06)'
                        : changed
                          ? 'rgba(99,102,241,0.05)'
                          : 'transparent',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {a.name}
                        </Typography>
                        {a.alreadyOverridden && (
                          <Chip
                            size="small"
                            label="Override existente"
                            sx={{
                              height: 16,
                              fontSize: 10,
                              bgcolor: 'rgba(245,158,11,0.18)',
                              color: '#fbbf24',
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Semana {a.weekOffset + 1} · calculado: {formatDateEs(a.computedDate)}
                      </Typography>
                    </Box>
                    <TenantDateField
                      size="small"
                      value={value}
                      onChange={(v) =>
                        setAssignmentOverrides((prev) => ({
                          ...prev,
                          [a.templateAssignmentId]: v,
                        }))
                      }
                    />
                  </Stack>
                );
              })}
              {assignmentRows.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                  La plantilla no tiene tareas.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('templateEditor.wizard.dueTimeTip')}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {result
            ? t('templateEditor.generate.actions.close')
            : t('templateEditor.generate.actions.cancel')}
        </Button>
        {(step === 2 || step === 3) && !result && (
          <Button
            startIcon={<ArrowBackRounded />}
            onClick={() => setStep((s) => (s === 3 ? 2 : 1))}
            disabled={submitting}
          >
            {t('templateEditor.generate.actions.back')}
          </Button>
        )}
        {step === 1 && !result && (
          <Button
            variant="contained"
            endIcon={loadingPreview ? <CircularProgress size={14} /> : <ArrowForwardRounded />}
            onClick={handleNextToPreview}
            disabled={loadingPreview}
          >
            {t('templateEditor.generate.actions.next')}
          </Button>
        )}
        {step === 2 && !result && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardRounded />}
            onClick={() => void goToStep3()}
            disabled={submitting}
          >
            {t('templateEditor.generate.actions.next')}
          </Button>
        )}
        {step === 3 && !result && (
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <PlayArrowRounded />}
            onClick={handleSubmit}
            disabled={
              submitting ||
              regenLoading ||
              // S6·2: sin la casilla, el servidor lo rechazaría igual.
              (!confirmFit &&
                (fitNeedsConfirmation(regen).truncated > 0 ||
                  fitNeedsConfirmation(regen).unplaced > 0))
            }
          >
            {t('templateEditor.generate.confirm')}
          </Button>
        )}
        {result && (
          <Button variant="contained" onClick={onGenerated}>
            {t('templateEditor.generate.actions.done')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
